if ( typeof Enrollment !== 'function' ) {
  class Enrollment extends HTMLElement {
    constructor() {
      super();
      this.threshold = this.offsetTop;
      this.init();
    }

    init() {
      this.classList.add('has-loaded');
      window.addEventListener('scroll', this.configureVisibility.bind(this));
    }

    configureVisibility() {
      // show when scrolled into view
      if (window.scrollY >= this.threshold) {
        this.classList.add('is-visible');
      } else {
        this.classList.remove('is-visible');
      }
    }
  }

  if ( typeof customElements.get('enrollment-main') == 'undefined' ) {
		customElements.define('enrollment-main', Enrollment);
	}
}

if ( typeof EnrollmentModal !== 'function' ) {
  class EnrollmentModal extends HTMLElement {
    constructor() {
      super();
      this.navButtons = this.querySelectorAll('[data-js-enrollment-nav-button]');
      this.states = this.querySelectorAll('[data-js-enrollment-modal-state]');
      this.init();
    }

    init() {
      this.navButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const currentState = button.closest('[data-js-enrollment-modal-state]');
          const currentIndexAttr = currentState.getAttribute('data-js-enrollment-modal-state');
          let currentIndex = parseFloat(currentIndexAttr);
          let direction = button.getAttribute('data-js-enrollment-nav-button');
          this.renderState(currentIndex, direction);
        })
      });

      this.addEventListener("enrollment:state:update", function(event) {
        let currentIndex = event.detail.currentIndex;
        let direction = event.detail.direction;
        this.renderState(currentIndex, direction);
      }.bind(this));

      // Application modal: enable the "Continue" button once a returning-stylist option is chosen
      const continueButton = this.querySelector('[data-js-enrollment-application-continue]');
      if (continueButton) {
        this.querySelectorAll('[name="_application_returning"]').forEach((radio) => {
          radio.addEventListener('change', () => {
            continueButton.disabled = false;
            continueButton.classList.remove('disabled');
          });
        });
      }

      // Application modal: jump straight to a specific state (for branching paths)
      this.querySelectorAll('[data-js-enrollment-goto]').forEach((button) => {
        button.addEventListener('click', () => {
          this.showState(button.getAttribute('data-js-enrollment-goto'));
        });
      });

      // Mark that the user chose to start a new application over an existing one (flag carried to the app redirect)
      this.querySelectorAll('[data-js-enrollment-start-new]').forEach((button) => {
        button.addEventListener('click', () => {
          this.startNewApplication = true;
        });
      });

      // Application modal: submit/continue navigates to the enrollment application page
      this.querySelectorAll('[data-js-enrollment-application-redirect]').forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          let url = (button.getAttribute('data-js-enrollment-application-redirect') || '').trim();
          if (!url) return;
          // Pass the returning-stylist answer (yes/no) along so the app can persist it
          const returning = this.querySelector('[name="_application_returning"]:checked');
          if (returning) {
            const separator = url.indexOf('?') === -1 ? '?' : '&';
            url += separator + 'is_returning=' + encodeURIComponent(returning.value);
          }
          // If the user chose "Start New Application" despite an existing one, tell the app to reset it
          if (this.startNewApplication) {
            const separator = url.indexOf('?') === -1 ? '?' : '&';
            url += separator + 'start_new=true';
          }
          // Per-button flow (standard | denim …) set by whichever trigger opened the modal
          const flow = window.ENROLLMENT_FLOW || 'standard';
          const flowSeparator = url.indexOf('?') === -1 ? '?' : '&';
          url += flowSeparator + 'flow=' + encodeURIComponent(flow);
          window.location.href = url;
        });
      });

      // DEMO: Next → Shopify passwordless login (email prefilled) → return here → open "Start A New Application".
      // Scoped to the application modal only. Other states stay in place but are not part of this demo path.
      if (this.hasAttribute('data-js-enrollment-application-modal')) {
        // Remember the per-button flow of whichever trigger opened this modal (e.g. flow=denim).
        // Triggers without the attribute clear it, so the hero "Get Started" sends no flow.
        document.querySelectorAll('[data-popup-enrollment="enrollment-application-modal"]').forEach((trigger) => {
          trigger.addEventListener('click', () => {
            window.ENROLLMENT_FLOW = trigger.getAttribute('data-enrollment-flow') || 'standard';
          });
        });

        // Pressing Enter in the email field submits the <form>, which would reload the page and close
        // the modal. Intercept it and trigger the Next button instead (same as clicking it).
        const applicationForm = this.querySelector('[data-js-enrollment-application-form]');
        if (applicationForm) {
          applicationForm.addEventListener('submit', (event) => {
            event.preventDefault();
            const nextButton = applicationForm.querySelector('[data-js-enrollment-application-submit]');
            if (nextButton) nextButton.click();
          });
        }

        const loginButton = this.querySelector('[data-js-enrollment-login-redirect]');
        if (loginButton) {
          const emailInput = this.querySelector('[name="_application_email"]');
          const authDomain = this.getAttribute('data-js-auth-domain');

          const showFieldError = (input, message) => {
            input.classList.add('error');
            const block = input.closest('.form__block');
            let errorEl = block.querySelector('[data-js-enrollment-field-error]');
            if (!errorEl) {
              errorEl = document.createElement('div');
              errorEl.className = 'enrollment__application__field-error';
              errorEl.setAttribute('data-js-enrollment-field-error', '');
              errorEl.setAttribute('role', 'alert');
              block.appendChild(errorEl);
            }
            errorEl.textContent = message;
          };
          const clearFieldError = (input) => {
            input.classList.remove('error');
            const errorEl = input.closest('.form__block').querySelector('[data-js-enrollment-field-error]');
            if (errorEl) errorEl.remove();
          };

          if (emailInput) {
            emailInput.addEventListener('input', () => clearFieldError(emailInput));
          }

          loginButton.addEventListener('click', () => {
            const email = emailInput ? emailInput.value.trim() : '';
            if (emailInput) {
              if (!email) {
                showFieldError(emailInput, 'Email address is required.');
                emailInput.focus();
                return;
              }
              if (!emailInput.checkValidity()) {
                showFieldError(emailInput, 'Please enter a valid email address.');
                emailInput.focus();
                return;
              }
              clearFieldError(emailInput);
            }
            let returnPath = window.location.pathname + '?enroll=open';
            returnPath += '&flow=' + encodeURIComponent(window.ENROLLMENT_FLOW || 'standard');
            const returnTo = encodeURIComponent(returnPath);
            const hint = encodeURIComponent(email);
            window.location.href = `https://${authDomain}/customer_authentication/login?login_hint=${hint}&return_to=${returnTo}`;
          });
        }

        // Returning from login with ?enroll=open — open this modal at the right screen.
        // A guard state (5 = active stylist, 6 = pending application) takes precedence over the
        // normal target (2 = Existing Application Found, 3 = Start A New Application).
        const params = new URLSearchParams(window.location.search);
        if (params.get('enroll') === 'open') {
          const guardState = this.getAttribute('data-js-enrollment-guard-state');
          const targetState = guardState || this.getAttribute('data-js-enrollment-target-state') || '3';
          window.addEventListener('load', () => {
            const trigger = document.querySelector('[data-popup-enrollment="enrollment-application-modal"]');
            if (trigger) trigger.click();
            // The auto-click fires the per-button capture and can pollute the flow with the first
            // trigger's value — always reset it from the carried-through URL value (default standard).
            window.ENROLLMENT_FLOW = params.get('flow') || 'standard';
            this.showState(targetState);
          });
        }

        // Find / change stylist → open the existing stylist finder, then return to the stylist step
        this.querySelectorAll('[data-js-enrollment-find-stylist]').forEach((button) => {
          button.addEventListener('click', () => {
            window.ENROLLMENT_FINDING_STYLIST = true;
            if (window.closeModalManually) window.closeModalManually('enrollment-application-modal');
            if (window.MicroModal) {
              // onClose only fires on a genuine cancel — a successful pick closes the finder via
              // closeModalManually (not MicroModal), so this clears the flag if the user backs out.
              window.MicroModal.show('find-your-consultant', {
                onClose: function () { window.ENROLLMENT_FINDING_STYLIST = false; }
              });
            }
          });
        });

        // When the finder finishes a selection (any pick, even the same stylist/site), reopen this modal
        // on the stylist step. The finder dispatches this after every selection — unlike theme:partner:render,
        // which only fires on a site change (so re-picking the same stylist wouldn't reopen otherwise).
        document.addEventListener('enrollment:stylist:selected', () => {
          window.ENROLLMENT_FINDING_STYLIST = false;
          if (window.MicroModal) window.MicroModal.show('enrollment-application-modal');
          this.showState(3);
        });

        // Confirm the chosen stylist → persist it as the current/selected stylist, then go to the Yes/No step
        this.querySelectorAll('[data-js-enrollment-stylist-confirm]').forEach((button) => {
          button.addEventListener('click', () => {
            const stylistStep = button.closest('[data-js-enrollment-stylist-step]');
            const choice = stylistStep && stylistStep.querySelector('input[name="_application_stylist_choice"]:checked');
            let partnerToApply;
            if (choice) {
              partnerToApply = choice.value === 'preferred' ? this.preferredStylist : this.selectedStylist;
            } else {
              partnerToApply = this.selectedStylist || this.preferredStylist;
            }
            if (partnerToApply) this.applySelectedStylist(partnerToApply);
            this.showState('4');
          });
        });

        // Continue Without A Stylist → drop the stylist from storage, then go to the Yes/No step
        this.querySelectorAll('[data-js-enrollment-without-stylist]').forEach((button) => {
          button.addEventListener('click', () => {
            this.clearSelectedStylist();
            this.showState('4');
          });
        });
      }

    }

    // Drop the current/selected stylist — mirrors the "remove consultant" action (custom.js)
    clearSelectedStylist() {
      localStorage.removeItem('CURRENT_SITE');
      localStorage.removeItem('CURRENT_SHOW_ID');
      localStorage.removeItem('CURRENT_SHOW_SITE');
      localStorage.removeItem('CURRENT_SHOW_NAME');
      localStorage.removeItem('CURRENT_SHOW_HOSTESS_NAME');
      localStorage.removeItem('CURRENT_STYLIST_ID');
      localStorage.removeItem('CURRENT_STYLIST_TYPE');
      localStorage.removeItem('CURRENT_SHOW_EVENT_IDS');
      localStorage.removeItem('CURRENT_ATTRIBUTION_SOURCE');
      localStorage.removeItem('CURRENT_ATTRIBUTION_SET_AT');
      localStorage.removeItem('CURRENT_ATTRIBUTED_SITE_STAMP');
      sessionStorage.removeItem('PREFERRED_SITE');
      try {
        const url = new URL(window.location);
        url.searchParams.delete('pws');
        url.searchParams.delete('show');
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* ignore */ }
      window.partner = null;
      document.dispatchEvent(new CustomEvent('theme:partner:render', { detail: { partner: null } }));
    }

    // Persist a stylist as the current/selected one (updates the black bar via theme:partner:render, no reload).
    applySelectedStylist(partner) {
      if (!partner || !partner.siteName) return;
      // CURRENT_STYLIST_ID has to be (re)written on every confirm, even when the
      // site itself hasn't changed — the applicant landing via this stylist's own
      // referral link is the common case, and the site-change guard below used to
      // skip this write for it entirely, silently dropping the enrollment sponsor
      // (CAB-3665: page.enrollment-application.liquid reads this key to build the
      // sponsor_stylist_id param the enrollment app receives).
      if (partner.stylistId) localStorage.setItem('CURRENT_STYLIST_ID', partner.stylistId);
      if (localStorage.getItem('CURRENT_SITE') === partner.siteName) return;
      // An explicit pick ends any "continue without a stylist" opt-out, which
      // attribution.js otherwise honours by withholding attribution on the next
      // page load — see find-your-consultant-modal.liquid for the same clear.
      localStorage.removeItem('STYLIST_OPT_OUT');
      localStorage.setItem('CURRENT_SITE', partner.siteName);
      localStorage.setItem('CURRENT_STYLIST_TYPE', window.backend.deriveStylistType(partner.tags));
      try {
        const url = new URL(window.location);
        url.searchParams.set('pws', partner.siteName);
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* ignore */ }
      window.partner = partner;
      document.dispatchEvent(new CustomEvent('theme:partner:render', { detail: { partner } }));
    }

    renderState(currentIndex, direction) {
      const targetIndex = direction === 'forward' ? currentIndex + 1 : currentIndex - 1;
      this.showState(targetIndex);
    }

    showState(targetIndex) {
      this.states.forEach((state) => { state.classList.add('hide'); });
      const targetState = this.querySelector(`[data-js-enrollment-modal-state="${targetIndex}"]`);
      if (targetState) {
        targetState.classList.remove('hide');
        const stylistStep = targetState.querySelector('[data-js-enrollment-stylist-step]') ||
          (targetState.hasAttribute('data-js-enrollment-stylist-step') ? targetState : null);
        if (stylistStep) this.populateStylistStep(stylistStep);
      }
    }

    // Fill the referring-stylist step from the selected stylist (same source as the black bar)
    // and the preferred stylist (customer metafield), then show the matching variant.
    async populateStylistStep(step) {
      const setText = (selector, value) => {
        step.querySelectorAll(selector).forEach((el) => { el.textContent = value || ''; });
      };

      // Selected stylist — window.partner (resolved by attribution.js), fall back to CURRENT_SITE lookup
      let selected = window.partner || null;
      if (!selected && window.backend) {
        const site = localStorage.getItem('CURRENT_SITE');
        if (site) selected = await window.backend.getPartnerBySite(site).catch(() => null);
      }

      // Preferred stylist — resolve the customer's preferred_stylist_id metafield
      let preferred = null;
      const preferredId = window.customer && window.customer.preferredStylistId;
      if (preferredId && window.backend) {
        preferred = await window.backend.getPartnerByStylistId(preferredId).catch(() => null);
      }

      // Only Active stylists are eligible — skip Former, Pending Termination, etc.
      // (an ineligible stylist is never shown, never confirmable, and so never sent as sponsor)
      if (selected && selected.status !== 'Active') selected = null;
      if (preferred && preferred.status !== 'Active') preferred = null;

      // Remember the resolved stylists so the Continue/confirm handler can persist the choice
      this.selectedStylist = selected;
      this.preferredStylist = preferred;

      if (selected) {
        setText('[data-js-selected-stylist-name]', selected.displayName);
        setText('[data-js-selected-stylist-email]', selected.email);
      }
      if (preferred) {
        setText('[data-js-preferred-stylist-name]', preferred.displayName);
        setText('[data-js-preferred-stylist-email]', preferred.email);
      }

      let variant = 'none';
      if (selected) {
        const mismatch = preferred && preferred.stylistId && selected.stylistId &&
          preferred.stylistId !== selected.stylistId;
        variant = mismatch ? 'mismatch' : 'selected';
      } else if (preferred) {
        variant = 'preferred';
      }

      step.querySelectorAll('[data-js-stylist-variant]').forEach((el) => {
        el.classList.toggle('hide', el.getAttribute('data-js-stylist-variant') !== variant);
      });
    }
  }

  if ( typeof customElements.get('enrollment-modal') == 'undefined' ) {
		customElements.define('enrollment-modal', EnrollmentModal);
	}
}

if ( typeof EnrollmentForm !== 'function' ) {
  class EnrollmentForm extends HTMLElement {
    constructor() {
			super();

      this.modal = this.closest('enrollment-modal');

      this.productToggles = this.querySelectorAll('[data-js-enrollment-toggle="product"]')
      this.donationToggles = this.querySelectorAll('[data-js-enrollment-toggle="donation"]');
      this.questionnaireForm = this.querySelector('[data-js-enrollment-questionnaire-form]');
      this.submit = this.querySelector('[data-js-enrollment-submit]');

      this.formData = [];

      this.init();
    }

    init() {
      this.productToggles.forEach((toggle) => {
        toggle.addEventListener('click', this.onToggleProductSelect.bind(this));
      });
      this.donationToggles.forEach((toggle) => {
        toggle.addEventListener('click', this.onToggleDonationSelect.bind(this));
      });
      this.questionnaireForm.addEventListener('change', this.configureSubmitButtonState.bind(this))
      this.submit.addEventListener('click', this.onFormSubmit.bind(this));
    }

    onToggleProductSelect(evt) {
      const toggle = evt.currentTarget;
      const parent = toggle.closest('[data-js-enrollment-selectable="product"]');

      const variantIdAttr = parent.getAttribute('data-js-variant-id');
      this.variantId = variantIdAttr ? parseFloat(variantIdAttr) : null;

      const data = {
        id: this.variantId,
        quantity: 1,
        _helpers: {
          _type: 'product'
        }
      }

      const found = this.formData.find((item) => item.id === this.variantId);
      if (found) {
        // remove current
        this.formData = this.formData.filter((item) => item.id !== this.variantId);
      } else {
        // remove others
        this.formData = this.formData.filter((item) => item._helpers?._type !== 'product');
        // add current
        this.formData.push(data);
        // update state
        this.fireStateChange(1);
      }

      toggle.blur();
      this.configureToggles('product');
    }

    onToggleDonationSelect(evt) {
      const toggle = evt.currentTarget;
      const parent = toggle.closest('[data-js-enrollment-selectable="donation"]');

      const donationIdAttr = parent.getAttribute('data-js-id');
      const donationVariantIdAttr = parent.getAttribute('data-js-variant-id');
      this.donationId = donationIdAttr ? parseFloat(donationIdAttr) : null;
      this.donationVariantId = donationVariantIdAttr ? parseFloat(donationVariantIdAttr) : null;

      const data = {
        id: this.donationVariantId,
        quantity: 1,
        _helpers: {
          _type: 'donation',
          _id: this.donationId
        }
      }

      const found = this.formData.find((item) => item._helpers?._id === this.donationId);
      if (found) {
        // remove current
        this.formData = this.formData.filter((item) => item._helpers?._id !== this.donationId);
      } else {
        // remove others
        this.formData = this.formData.filter((item) => item._helpers?._type !== 'donation');
        // add current
        this.formData.push(data);
        // update state
        this.fireStateChange(2);
      }

      toggle.blur();
      this.configureToggles('donation');
    }

    onFormSubmit() {
      const questionnaireFormData = new FormData(this.questionnaireForm);

      const items = this.formData.map((item) => {
        let commonProperties = { _set: 'enrollment' };
        let additionalProperties = {};
        if (item._helpers?._type === 'product') { 
          additionalProperties = Object.fromEntries(questionnaireFormData);
        }
        if (item._helpers?._type === 'donation' && item._helpers?._id) {
          additionalProperties = { _donation_id: item._helpers?._id };
        }

        return {
          id: item.id,
          quantity: item.quantity,
          properties: { ...commonProperties, ...additionalProperties }
        }
      });

      const payload = {
        items: items
      }

      if (this.formData.length) {
        this.submit.classList.add('loading');

        fetch(`${window.theme.routes.cart}/add.js`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify(payload)
        })
        .then((res) => res.json())
        .then((res) => {
          if (res.status == 422) {
            // TODO: handle stockout error
            return false;
          }
          
          this.submit.classList.remove('loading');
          this.submit.classList.add('has-success');

          setTimeout(() => {
            this.submit.classList.remove('has-success');
            this.submit.disabled = false;

            // reload cart
            document.dispatchEvent(new CustomEvent("theme:cart:init"));
            // redirect to checkout
            window.location.href = '/checkout';
          }, 1500);
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.submit.classList.remove('loading');
        })
      }
    }

    fireStateChange(index) {
      this.modal.dispatchEvent(
        new CustomEvent("enrollment:state:update", { 
          detail: { 
            currentIndex: index,
            direction: 'forward'
          } 
        }
      ));
    }

    validateQuestionnaireForm() {
      const radioFields = ['_q1', '_q2', '_q4'];
      const selectFields = ['_q3'];
      let valid = false;

      const radiosValid = radioFields.every((field) => {
        const checked = this.questionnaireForm.querySelector(`input[name="${field}"]:checked`);
        return checked;
      });
      const selectsValid = selectFields.every((field) => {
        const helperInput = this.questionnaireForm.querySelector(`input[name="${field}"]`);
        return helperInput && helperInput.value.trim() !== '';
      })
      if (radiosValid && selectsValid) {
        valid = true;
      }
      return valid;
    }

    configureSubmitButtonState() {
      let questionnaireValid = this.validateQuestionnaireForm();

      if (questionnaireValid) {
        this.submit.classList.remove('disabled');
        this.submit.removeAttribute('disabled');
      } else {
        this.submit.classList.add('disabled');
        this.submit.setAttribute('disabled', 'disabled');
      }
    }

    configureToggles(type) {
      const toggles = type === 'donation' ? this.donationToggles : this.productToggles;
      
      toggles.forEach((toggle) => {
        const toggleTextSelect = toggle.querySelector('[data-js-enrollment-toggle-text="select"]');
        const toggleTextUnselect = toggle.querySelector('[data-js-enrollment-toggle-text="unselect"]');

        const parent = toggle.closest(`[data-js-enrollment-selectable="${type}"]`);
        const useIdAttrName = type === 'donation' ? 'data-js-id' : 'data-js-variant-id';
        const useIdAttr = parent.getAttribute(useIdAttrName);
        const useId = useIdAttr ? parseFloat(useIdAttr) : null;

        const found = this.formData.find((item) =>
          type === 'donation' ? item._helpers?._id === useId : item.id === useId
        );
        if (found) {
          toggleTextSelect.classList.add('hide');
          toggleTextUnselect.classList.remove('hide');
        } else {
          toggleTextUnselect.classList.add('hide');
          toggleTextSelect.classList.remove('hide');
        }
      });
    }
  }

  if ( typeof customElements.get('enrollment-form') == 'undefined' ) {
		customElements.define('enrollment-form', EnrollmentForm);
	}
}
